import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { Users, UserCheck, Clock, FileText, Download, TrendingUp, DollarSign, AlertCircle, ArrowRight, RefreshCw, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [pending, setPending] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [renewalResult, setRenewalResult] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/expiring-soon", { params: { days: 15 } }).then(({ data }) => setExpiring(data));
    api.get("/admin/pending-activations").then(({ data }) => setPending(data));
  };
  useEffect(() => { loadAll(); }, []);

  const activate = async (a) => {
    if (!window.confirm(`Activate ${a.agent_name || a.email} on ${a.requested_plan_name || a.requested_plan || 'default plan'}?\nThis records the offline payment and sends a confirmation email.`)) return;
    setActivatingId(a.id);
    try {
      await api.post(`/admin/agents/${a.id}/activate-offline`, {});
      toast.success("Agent activated — email sent");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    } finally {
      setActivatingId(null);
    }
  };

  if (!stats) return <div className="p-10">Loading…</div>;

  const items = [
    { label: "Total Registered Agents", value: stats.total_agents, icon: Users, testid: "stat-total-agents", big: true },
    { label: "Active Agents", value: stats.active_agents, icon: UserCheck, testid: "stat-active" },
    { label: "Pending Activations", value: stats.pending_activations, icon: Clock, testid: "stat-pending" },
    { label: "Total Quotations", value: stats.total_quotations, icon: FileText, testid: "stat-quotations" },
    { label: "PDF Downloads", value: stats.pdf_downloads, icon: Download, testid: "stat-pdfs" },
    { label: "Monthly Registrations", value: stats.monthly_registrations, icon: TrendingUp, testid: "stat-monthly" },
    { label: "Subscription Revenue", value: `₹${Number(stats.revenue).toLocaleString()}`, icon: DollarSign, testid: "stat-revenue", accent: true },
  ];

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-8 lg:p-12">
        <div className="max-w-6xl">
          <div className="overline">Admin console</div>
          <h1 className="font-editorial text-4xl mt-2">Overview</h1>
          <p className="text-sm text-slate-500 mt-2">Real-time platform health and revenue.</p>

          <div className="mt-10 grid md:grid-cols-4 gap-4">
            <div data-testid={items[0].testid} className="md:col-span-2 md:row-span-2 bg-[#0F1E5B] text-white rounded p-8 noise-bg">
              <div className="overline !text-[#BBF7D0]">{items[0].label}</div>
              <div className="font-editorial text-7xl mt-6">{items[0].value}</div>
              <div className="mt-6 text-sm text-white/70">
                {stats.active_agents} active · {stats.pending_activations} pending
              </div>
            </div>
            {items.slice(1).map((s) => (
              <div key={s.label} data-testid={s.testid} className={`rounded p-6 ${s.accent ? "bg-[#16A34A] text-white" : "bg-white border border-border"}`}>
                <div className={`overline ${s.accent ? "!text-[#BBF7D0]" : ""}`}>{s.label}</div>
                <div className="font-editorial text-3xl mt-3">{s.value}</div>
                <s.icon size={18} className={`mt-3 ${s.accent ? "text-[#BBF7D0]" : "text-[#16A34A]"}`} />
              </div>
            ))}
          </div>

          {pending.length > 0 && (
            <div data-testid="pending-activations-panel" className="mt-10 bg-white border border-border rounded overflow-hidden">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-emerald-50">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-emerald-700" />
                  <div className="font-editorial text-lg">Pending activations — offline payments awaiting approval</div>
                </div>
                <div className="text-xs uppercase tracking-widest text-emerald-800">{pending.length} agent{pending.length===1?"":"s"}</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-6 py-3">Agent</th>
                    <th className="text-left px-6 py-3">Contact</th>
                    <th className="text-left px-6 py-3">Requested plan</th>
                    <th className="text-left px-6 py-3">Requested at</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((a) => (
                    <tr key={a.id} className="border-t border-border hover:bg-slate-50">
                      <td className="px-6 py-3">
                        <div className="font-medium">{a.agent_name || "—"}</div>
                        <div className="text-xs text-slate-500">{a.agency_name || "—"}</div>
                      </td>
                      <td className="px-6 py-3 text-xs">
                        <div>{a.email}</div>
                        <div className="text-slate-500">{a.mobile || "—"}</div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="font-medium text-sm">{a.requested_plan_name || a.requested_plan || "—"}</div>
                        <div className="text-xs text-slate-500">{a.requested_plan_price ? `₹${Number(a.requested_plan_price).toLocaleString()}` : ""}</div>
                      </td>
                      <td className="px-6 py-3 text-xs text-slate-600">
                        {a.activation_requested_at ? new Date(a.activation_requested_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`pending-activate-${a.id}`}
                            onClick={() => activate(a)}
                            disabled={activatingId === a.id}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 bg-[#16A34A] text-white rounded hover:bg-emerald-700 disabled:opacity-60"
                          >
                            <CheckCircle2 size={12} /> {activatingId === a.id ? "Activating…" : "Activate"}
                          </button>
                          <Link data-testid={`pending-view-${a.id}`} to={`/admin/agents/${a.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 hover:text-[#16A34A]">View <ArrowRight size={12}/></Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {expiring.length > 0 && (
            <div data-testid="expiring-soon-panel" className="mt-10 bg-white border border-border rounded overflow-hidden">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-amber-50">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-600" />
                  <div className="font-editorial text-lg">Subscriptions expiring in ≤15 days</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs uppercase tracking-widest text-amber-700">{expiring.length} agent{expiring.length===1?"":"s"}</div>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-6 py-3">Agent</th>
                    <th className="text-left px-6 py-3">Agent ID</th>
                    <th className="text-left px-6 py-3">Expires</th>
                    <th className="text-left px-6 py-3">Email</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.map((a) => (
                    <tr key={a.id} className="border-t border-border hover:bg-slate-50">
                      <td className="px-6 py-3">
                        <div className="font-medium">{a.agent_name}</div>
                        <div className="text-xs text-slate-500">{a.agency_name}</div>
                      </td>
                      <td className="px-6 py-3 font-mono-e text-xs">{a.agent_id || "—"}</td>
                      <td className="px-6 py-3">{a.subscription_expiry ? new Date(a.subscription_expiry).toLocaleDateString() : "—"}</td>
                      <td className="px-6 py-3 text-xs text-slate-600">{a.email}</td>
                      <td className="px-6 py-3 text-right">
                        <Link data-testid={`expiring-manage-${a.id}`} to={`/admin/agents/${a.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#16A34A]">Manage <ArrowRight size={12}/></Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {renewalResult && (
                <div data-testid="renewal-result" className="border-t border-border bg-slate-50 px-6 py-4 text-xs">
                  <div className="overline text-[9px] mb-2">Last run</div>
                  <div className="space-y-1">
                    {renewalResult.results.map((r, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${r.status === "succeeded" ? "bg-emerald-100 text-emerald-800" : r.status === "needs_action" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{r.status.toUpperCase()}</span>
                        <span className="text-slate-700">{r.email}</span>
                        {r.reason && <span className="text-slate-400">— {r.reason}</span>}
                      </div>
                    ))}
                    {renewalResult.results.length === 0 && <div className="text-slate-500">No opted-in agents were eligible in the window.</div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
